import { DateInRangesValidator } from "@cervval.js/client-html5-ui/src/editors/editorsWithPicker/dateEditor/dateEditor.validators";
import { APP_SERVICE, AppService } from "@cervval.js/common/services";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { PropertyConfig, PropertyOptionsList } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { EmailValidator } from "@cervval.js/common/src/services/validation.impl/validators/email.validator";
import { MaxLengthValidator } from "@cervval.js/common/src/services/validation.impl/validators/maxLength.validator";
import { MinLengthValidator } from "@cervval.js/common/src/services/validation.impl/validators/minLength.validator";
import { PhoneNumberValidator } from "@cervval.js/common/src/services/validation.impl/validators/phoneNumber.validator";
import { RequiredValidator } from "@cervval.js/common/src/services/validation.impl/validators/required.validator";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { is, keyof } from "@cervval.js/common/src/tools/object.tools";
import { getTodayDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { TrustedPersonDTO } from "@sportsante/common-shared/src/data/users/trustedPerson.dto";
import { PatientDTO } from "@sportsante/common-shared/src/data/users/user.dto";
import { patientKeysSorted } from "@sportsante/common-shared/src/data/users/user.tools";
import { DataSourceUID } from "@sportsante/common-shared/src/datasource";
import { I18nPatient } from "@sportsante/common-shared/src/i18n/patient.i18n";

/**
 * Export SportSantePatientDTO into PropertyConfig
 * @param params Parameters object
 * @param params.canEdit Boolean. Set it to false to make all exported propertyConfig disabled. Default is `true`
 * @param params.disableFields Array of `PatientDTO` keys. A key present in this array will always have its propertyConfig disabled. Default is `[]`
 * @param params.includeFields Array of `PatientDTO` keys to include. The propertyConfig list will respect the order of this list, and duplicate keys will be ignored. Default is `patientKeysSorted`
 */
/* eslint-disable */
export function exportPatientDTOToPropertyConfig(
    params?: {
        canEdit?: boolean,
        disableFields?: (keyof PatientDTO)[],
        includeFields?: (keyof PatientDTO)[],
        optionalMail?: boolean,
        optionalAddress?: boolean,
        optionalPhone?: boolean
    }
): PropertyConfig[] {

    const canEdit = params?.canEdit ?? true;
    const disableFields = params?.disableFields ?? [];
    const includeFields = params?.includeFields ?? patientKeysSorted;
    const i18n = I18nPatient();
    const phoneLocale = injector.find<AppService>(APP_SERVICE)?.getLocale().phoneLocale ?? "FR";

    const disabledPropertySet = new Set<keyof PatientDTO>(disableFields);
    const alreadyAddedProperties = new Set<keyof PatientDTO>();

    const propertyConfigs: PropertyConfig[] = [];
    for (const field of includeFields) {
        const isDisabled = disabledPropertySet.has(field) ? true : canEdit === false;
        let propertyConfig = {} as PropertyConfig;
        const comp = propertyConfig;
        if (alreadyAddedProperties.has(field)) { continue; }
        switch (field) {
            case "birthDate":
                propertyConfig = {
                    label: i18n.birthDate.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.DATE,
                    options: {
                        placeholder: i18n.placeholders.required,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(DateInRangesValidator, {
                            ranges: [{
                                start: getTodayDate().beginOf("day").subtract({ year: 125 }).uts,
                                finish: getTodayDate().beginOf("day").subtract({ year: 18 }).uts
                            }]
                        })
                    };
                }
                break;
            case "gender":
                propertyConfig = <PropertyConfig<PropertyConfigType, PropertyOptionsList>>{
                    ...i18n.civility,
                    propertyName: field,
                    typeUid: PropertyConfigType.LISTSELECT,
                    options: {
                        itemList: DataSourceUID.CIVILITY,
                        emptyValue: { label: i18n.genderEmpty.label },
                        disabled: isDisabled,
                        filterDisabled: true
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true)
                    };
                }
                break;
            case "givenName":
                propertyConfig = {
                    label: i18n.givenName.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        placeholder: i18n.placeholders.required,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(MinLengthValidator, 3)
                    };
                }
                break;
            case "familyName":
                propertyConfig = {
                    label: i18n.familyName.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        placeholder: i18n.placeholders.required,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true),
                        ...validatorEntry(MinLengthValidator, 3)
                    };
                }
                break;
            case "email":
                propertyConfig = {
                    label: i18n.email.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.EMAIL,
                    options: {
                        placeholder: !params?.optionalMail ? i18n.placeholders.required : i18n.placeholders.optional,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, !params?.optionalMail),
                        ...validatorEntry(EmailValidator, true)
                    };
                }
                break;
            case "phoneNumber":
                propertyConfig = {
                    label: i18n.phoneNumber.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.PHONE_NUMBER,
                    options: {
                        disabled: isDisabled,
                        placeholder: !params?.optionalPhone ? i18n.placeholders.required : i18n.placeholders.optional
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, !params?.optionalPhone),
                        ...validatorEntry(PhoneNumberValidator, phoneLocale)
                    };
                }
                break;
            case "address":
                propertyConfig = {
                    label: i18n.address.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        disabled: isDisabled,
                        placeholder: !params?.optionalAddress ? i18n.placeholders.required : i18n.placeholders.optional
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, !params?.optionalAddress),
                        ...validatorEntry(MaxLengthValidator, 512)
                    };
                }
                break;
            case "postalCode":
                propertyConfig = {
                    label: i18n.postalCode.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        disabled: isDisabled,
                        placeholder: !params?.optionalAddress ? i18n.placeholders.required : i18n.placeholders.optional
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, !params?.optionalAddress),
                        ...validatorEntry(MaxLengthValidator, 128)
                    };
                }
                break;
            case "city":
                propertyConfig = {
                    label: i18n.city.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.STRING,
                    options: {
                        disabled: isDisabled,
                        placeholder: !params?.optionalAddress ? i18n.placeholders.required : i18n.placeholders.optional
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, !params?.optionalAddress),
                        ...validatorEntry(MaxLengthValidator, 128)
                    };
                }
                break;
            case "referralId":
                propertyConfig = <PropertyConfig<PropertyConfigType, PropertyOptionsList>>{
                    label: i18n.referral.label,
                    propertyName: field,
                    typeUid: PropertyConfigType.LISTSELECT,
                    options: {
                        itemList: DataSourceUID.COACH,
                        disabled: isDisabled
                    }
                };
                if (!isDisabled) {
                    propertyConfig.validators = {
                        ...validatorEntry(RequiredValidator, true)
                    };
                }
                break;
            default:
                break;
        }
        if (propertyConfig !== comp) {
            propertyConfigs.push(propertyConfig);
            alreadyAddedProperties.add(field);
        }
    }
    return propertyConfigs;
}
/* eslint-enable */
/** 
 * Return a property config to visualize / edit the MSS id of a patient
 * @param [canEdit=true] Boolean flag to know if the property should be editable. Default value is `true`
 */
export function exportIdMSSPropertyConfig(canEdit: boolean = true): PropertyConfig {
    const i18n = I18nPatient();
    return {
        label: i18n.idMss.label,
        propertyName: "idMSS",
        typeUid: PropertyConfigType.STRING,
        options: {
            disabled: !canEdit,
            placeholder: i18n.placeholders.optional
        },
        validators: !canEdit ? undefined : { ...validatorEntry(MaxLengthValidator, 256) }
    };
}

export type TrustedPersonKeysForPropertyConfig = "tp_givenName" | "tp_familyName" | "tp_bond" | "tp_phoneNumber";

/**
 * Export attributes of `TrustedPersonDTO` class to a list of `PropertyConfig`
 * @param canEdit Boolean to know if edition should be enabled on the `PropertyConfig`. Default to `true`
 * @param addRequired Boolean to know if the `RequiredValidator` should be enabled on `PropertyConfig`.
 * It can be interesting in some forms to control if the validator is set or not, especially when a `ConstraintHelper` handles the same interaction.
 * Default to `true`
 * @param useSpecialKeys Boolean to know if we should use the keys from type `TrustedPersonKeysForPropertyConfig` to reference the properties.
 * It can be useful if you use `TrustedPersonDTO` properties in a bigger form with other properties that can have the same name. Default to `false`
 */
export function exportTrustedPersonDTOToPropertyConfig(canEdit: boolean = true, useSpecialKeys: boolean = false): PropertyConfig[] {
    const i18n = I18nPatient();
    const phoneLocale = injector.find<AppService>(APP_SERVICE)?.getLocale().phoneLocale ?? "FR";

    return [
        {
            label: i18n.givenName.label,
            propertyName: useSpecialKeys ? <TrustedPersonKeysForPropertyConfig>("tp_givenName") : keyof<TrustedPersonDTO>("givenName"),
            typeUid: PropertyConfigType.STRING,
            options: {
                disabled: !canEdit,
                placeholder: i18n.placeholders.required
            },
            validators: {
                ...validatorEntry(RequiredValidator, canEdit),
                ...validatorEntry(MaxLengthValidator, 256)
            }
        }, {
            label: i18n.familyName.label,
            propertyName: useSpecialKeys ? is<TrustedPersonKeysForPropertyConfig>("tp_familyName") : keyof<TrustedPersonDTO>("familyName"),
            typeUid: PropertyConfigType.STRING,
            options: {
                disabled: !canEdit,
                placeholder: i18n.placeholders.required
            },
            validators: {
                ...validatorEntry(RequiredValidator, canEdit),
                ...validatorEntry(MaxLengthValidator, 256)
            }
        }, {
            label: i18n.bond.label,
            propertyName: useSpecialKeys ? is<TrustedPersonKeysForPropertyConfig>("tp_bond") : keyof<TrustedPersonDTO>("bond"),
            typeUid: PropertyConfigType.STRING,
            options: {
                disabled: !canEdit,
                placeholder: i18n.placeholders.required
            },
            validators: {
                ...validatorEntry(RequiredValidator, canEdit),
                ...validatorEntry(MaxLengthValidator, 64)
            }
        }, {
            label: i18n.phoneNumber.label,
            propertyName: useSpecialKeys ? is<TrustedPersonKeysForPropertyConfig>("tp_phoneNumber") : keyof<TrustedPersonDTO>("phoneNumber"),
            typeUid: PropertyConfigType.PHONE_NUMBER,
            options: {
                disabled: !canEdit,
                placeholder: i18n.placeholders.optional
            },
            validators: { ...validatorEntry(PhoneNumberValidator, phoneLocale) }
        }
    ];
}